import { Injectable } from '@angular/core'
import { BehaviorSubject, EMPTY, Observable, interval } from 'rxjs'
import { distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'

export interface AlertItem {
  category: string
  visible: boolean
  message: string
  deleted?: boolean
  alertId?: string
  createdAt?: string
}

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private dataSubject = new BehaviorSubject<AlertItem[]>([])
  data$ = this.dataSubject.asObservable()

  private showBannerSubject = new BehaviorSubject<boolean>(false)
  showPreviewBanner$ = this.showBannerSubject.asObservable()

  constructor(
    private apiService: ApiService,
    private uiService: UiService
  ) {
    this.loadInitialData()
  }
  /**important to initially load Alerts data */
  public loadInitialData() {
    interval(60000)
      .pipe(
        // Keep polling the API every 60000ms = 1 minute
        startWith(0), // This ensures it fetches immediately on service load
        switchMap(() => this.apiService.getAlerts()), // Call the API
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)), // Only proceed if the data has changed
        tap((details) => this.dataSubject.next(details)) // Update the dataSubject
      )
      .subscribe(
        // Start the subscription to kick off the polling
        () => {}
        // We don't use handleArpioError here.  The alerts URL lacks account
        // context, so the server can't give us an authentication URL with
        // the right settings for whatever account-related page these alerts
        // may be getting shown on.  We'll let this just fail in the background
        // (there will be no alerts to show and that's OK).
      )
  }

  getData(): Observable<AlertItem[]> {
    return this.data$
  }

  /** Add and Update an existing Alert, it uses a PUT */
  updateAlerts(category: string, updatedItem: AlertItem): Observable<any> {
    return this.apiService.updateAlerts([updatedItem]).pipe(
      tap((updatedData) => {
        this.dataSubject.next(updatedData)
        this.loadInitialData()
      })
    )
  }

  getCategories(): Observable<string[]> {
    return this.data$.pipe(map((data) => Array.from(new Set(data.map((item) => item.category)))))
  }

  getFilteredData(): Observable<AlertItem[]> {
    return this.data$.pipe(map((data) => data.filter((item) => !item.deleted)))
  }

  onshowBannerChange(showBanner: boolean) {
    this.showBannerSubject.next(showBanner)
  }
}
